import { useState } from "react";

/** Nextjs */
import dynamic from "next/dynamic";

/** MUI */
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    Popover,
    Typography,
    alpha,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
/** - icons */
import { Close, ExpandMore, InfoOutlined, SaveAlt } from "@mui/icons-material";

/** Helpers */
import { saveAs } from "file-saver";
import JSZip from "jszip";
import moment from "moment";
import { useTranslation } from "react-i18next";
import useHelper from "../../../_common/useHelper";
import { makePDF } from "./toPDF/jsPDF_make";

/** API */
import { getMetrykaCSVFromAPI } from "../../../_APIs";

/** UI */
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import "overlayscrollbars/overlayscrollbars.css";

const AppTooltip = dynamic(() => import("../../../_common/AppTooltip"));
const CertificateContent = dynamic(() => import("./CertificateContent"));

const useStyles = (props) =>
    makeStyles((theme) => {
        return {
            rootBox: {
                height: "calc(100vh - 70px)",
                overflow: "hidden",
                position: "relative",
                paddingTop: theme.spacing(0.5),
                paddingRight: theme.spacing(1),
                [theme.breakpoints.down("md")]: {
                    paddingRight: 0,
                },
            },
            osScrollbar: {
                height: "calc(100vh - 182px)",
                paddingRight: theme.spacing(1),
                [theme.breakpoints.down("md")]: {
                    paddingRight: theme.spacing(0),
                },
                [theme.breakpoints.down("sm")]: {
                    height: "calc(100vh - 166px)",
                },
                "& > .os-scrollbar > .os-scrollbar-track > .os-scrollbar-handle, & > .os-scrollbar:hover > .os-scrollbar-track > .os-scrollbar-handle":
                    {
                        background: "#919090",
                    },
                "& > .os-scrollbar.os-scrollbar-vertical": {
                    padding: 1,
                    width: 6,
                    transition: "all 0.125s linear",
                    background: alpha(theme.palette.primary.main, 0.05),
                    borderRadius: 16,
                    "&:hover": {
                        width: 16,
                        padding: 2,
                        cursor: "s-resize",
                    },
                },
            },
            header: {
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
            },
            title: {
                flexGrow: 1,
                display: "flex",
                alignItems: "center",
                fontWeight: 600,
                fontSize: "1.33333333rem",
                // paddingLeft: theme.spacing(2),
                paddingLeft: theme.spacing(0),
                [theme.breakpoints.down("lg")]: {
                    paddingLeft: theme.spacing(1),
                    fontSize: "1.11111111rem",
                },
                [theme.breakpoints.down("sm")]: {
                    paddingLeft: theme.spacing(1),
                    fontSize: "1rem",
                },
            },
            titleSecondary: {
                flexGrow: 1,
                display: "flex",
                alignItems: "center",
                fontWeight: 600,
                fontSize: "1.11111111rem",
                [theme.breakpoints.down("lg")]: {
                    fontSize: "0.88888889rem",
                },
            },
            summaryRoot: {
                paddingRight: 0,
                paddingLeft: theme.spacing(1),
                "&$expanded": {
                    "& $titleSecondary": {
                        fontSize: "1.11111111rem",
                        [theme.breakpoints.down("lg")]: {
                            fontSize: "1.11111111rem",
                        },
                    },
                },
                [theme.breakpoints.down("md")]: {
                    paddingLeft: theme.spacing(1),
                },
            },
            summaryContent: {
                "&$expanded": {
                    margin: 0,
                },
            },
            expanded: {},
            AllFormControlLabel: {
                margin: 0,
                paddingRight: 45,
                marginTop: theme.spacing(3),
                [theme.breakpoints.down("md")]: {
                    paddingRight: 37,
                    marginTop: theme.spacing(1),
                },
                [theme.breakpoints.down("sm")]: {
                    width: "100%",
                },
            },
            AllFormControlLabelLabel: {
                [theme.breakpoints.down("lg")]: {
                    fontSize: "0.88888889rem",
                },
            },
            popoverRoot: {
                zIndex: 3211,
            },
            popoverPaper: {
                ...theme.shape.boxedWrapper,
                maxWidth: 300,
                minWidth: 150,
            },
            preloader: {
                position: "absolute",
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                backgroundColor: "rgba(255, 255, 255, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "& img": {
                    opacity: 0.5,
                    width: 128,
                    height: 128,
                },
            },
        };
    });

const CertificateAccordion = (props) => {
    const { color = "secondary", data, onClose } = props;

    const classes = useStyles({ color })();
    const { i18n, t } = useTranslation();
    const { translationFromJson } = useHelper();

    /** States */
    const [anchorEl, setAnchorEl] = useState(null);
    const [EXPANDED, setEXPANDED] = useState(false);
    const [EXPORTING, setEXPORTING] = useState(false);
    const [CHECKED, setCHECKED] = useState([]);

    /** Handlers */
    const handleExpand = (panel) => setEXPANDED(EXPANDED === panel ? false : panel);

    const handleCheck = (event) => {
        const _value = event.target.value;
        const _checked = CHECKED.includes(_value);

        setCHECKED(_checked ? CHECKED.filter((c) => c !== _value) : [...CHECKED, _value]);
    };

    const handleCheckAll = () => setCHECKED(CHECKED?.length === data.length ? [] : data.map((i) => `${i.id_zmienna}`));

    /** - popover */
    const handleOpenPopover = (event) => setAnchorEl(event.currentTarget);
    const handleClosePopover = () => setAnchorEl(null);
    const open = Boolean(anchorEl);
    const id = open ? "save-popover" : undefined;
    /** / - popover */

    const handleExport2PDF = () => {
        handleClosePopover();
        setEXPORTING(true);
        setTimeout(() => {
            makePDF({
                pdfOptions: data
                    ?.filter((d) => CHECKED?.includes(d?.id_zmienna?.toString()))
                    ?.map((d) => ({
                        data: d,
                        language: i18n.language,
                        translationFromJson,
                    })),
            });
        }, 1000);
        setTimeout(() => {
            setEXPORTING(false);
        }, 3000);
    };

    const handleExportZipPDF = () => {
        handleClosePopover();
        setEXPORTING(true);

        const ZIP = new JSZip();

        data?.filter((d) => CHECKED?.includes(d?.id_zmienna?.toString()))?.forEach((d, i) => {
            const _blobPDF = makePDF({
                pdfOptions: [
                    {
                        data: d,
                        language: i18n.language,
                        translationFromJson,
                    },
                ],
                asBlob: true,
            });
            console.log({ _blobPDF });

            const filename = `DBW-${t("metainfo")}-[${d.id_zmienna}]-${moment().format("YYYYMMDD.HHmm")}${
                i18n.language ? `.${i18n.language?.toLowerCase()}` : ""
            }`;

            ZIP.file(`${filename}.pdf`, _blobPDF, { binary: true });
        });

        ZIP.generateAsync({ type: "blob" }, function updateCallback(metadata) {}).then(
            function callback(blob) {
                saveAs(blob, "metainfos.zip");
            },
            function (e) {}
        );

        setTimeout(() => {
            setEXPORTING(false);
        }, 3000);
    };

    const handleExport2CSV = () => {
        handleClosePopover();
        setEXPORTING(true);

        getMetrykaCSVFromAPI({ indicators: CHECKED })
            .then((res) => {
                process.env.NODE_ENV === "development" &&
                    console.log("%c getMetrykaCSVFromAPI: ", "background: #666; color:#FFF", { res });

                if (res.blobed && res.result?.errcode === 200) {
                    const _filename = `DBW-${t("metainfo")}-[${CHECKED?.join(",")}]-${moment().format(
                        "YYYYMMDD.HHmm"
                    )}${i18n.language ? `.${i18n.language?.toLowerCase()}` : ""}.csv`;

                    let blob = res.blobed;
                    saveAs(blob, _filename);
                } else setERROR(res.result);
            })
            .catch((error) => {
                console.log(error);
            })
            .finally(() => {});

        setTimeout(() => {
            setEXPORTING(false);
        }, 3000);
    };

    return (
        <Box className={classes.rootBox}>
            <Grid container spacing={0} sx={{ position: "relative", height: "100%" }}>
                <Grid item xs={12} className={classes.header}>
                    <Typography variant={"h6"} align="left" className={classes.title} component={"h2"}>
                        <InfoOutlined sx={{ width: 28, height: 28, mr: 2, mt: 0.25 }} />
                        {t("Metadane")}
                    </Typography>
                    <Box sx={{ minWidth: 86, mt: "-5px" }}>
                        <AppTooltip
                            title={t("Eksportuj metadane do pliku CSV lub PDF")}
                            arrow
                            placement={"top-end"}
                            type={"dark"}
                            classes={{ tooltip: classes.tooltip }}
                        >
                            <IconButton
                                aria-label={t(
                                    "Eksportuj metadane dla zaznaczonych poniżej zmiennych, do pliku CSV lub PDF"
                                )}
                                size={"small"}
                                onClick={handleOpenPopover}
                                sx={{ width: 40, height: 40 }}
                            >
                                <SaveAlt color={color} />
                            </IconButton>
                        </AppTooltip>

                        <Popover
                            id={id}
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleClosePopover}
                            anchorOrigin={{
                                vertical: "top",
                                horizontal: "left",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            classes={{ root: classes.popoverRoot, paper: classes.popoverPaper }}
                        >
                            <Button
                                fullWidth
                                sx={{ justifyContent: "flex-start" }}
                                disabled={!CHECKED?.length}
                                onClick={handleExport2CSV}
                                startIcon={
                                    <Box
                                        component={"img"}
                                        src={"/icons/export/file-csv.png"}
                                        alt=""
                                        sx={{
                                            width: 24,
                                            height: "auto",
                                            opacity: CHECKED?.length ? 1 : 0.5,
                                            filter: `grayscale(${CHECKED?.length ? 0 : 1})`,
                                        }}
                                    />
                                }
                            >
                                {t("CSV")}
                            </Button>

                            <Divider light sx={{ my: 1 }} />

                            <Button
                                onClick={handleExport2PDF}
                                fullWidth
                                sx={{ justifyContent: "flex-start" }}
                                disabled={!CHECKED?.length}
                                startIcon={
                                    <Box
                                        component={"img"}
                                        src={"/icons/export/file-pdf.png"}
                                        alt=""
                                        sx={{
                                            width: 24,
                                            height: "auto",
                                            opacity: CHECKED?.length ? 1 : 0.5,
                                            filter: `grayscale(${CHECKED?.length ? 0 : 1})`,
                                        }}
                                    />
                                }
                            >
                                {t("PDF")}&nbsp;
                                {CHECKED?.length > 1 ? (
                                    <Box component={"span"} sx={{ textTransform: "initial", fontWeight: 400 }}>
                                        -&nbsp;{t("jeden plik (*{{dot}}pdf)", { dot: "." })}
                                    </Box>
                                ) : null}
                            </Button>
                            {CHECKED?.length > 1 ? (
                                <Button
                                    onClick={handleExportZipPDF}
                                    fullWidth
                                    sx={{ justifyContent: "flex-start" }}
                                    disabled={!CHECKED?.length}
                                    startIcon={
                                        <Box
                                            component={"img"}
                                            src={"/icons/export/files-pdf.png"}
                                            alt=""
                                            sx={{
                                                width: 24,
                                                height: "auto",
                                                opacity: CHECKED?.length ? 1 : 0.5,
                                                filter: `grayscale(${CHECKED?.length ? 0 : 1})`,
                                            }}
                                        />
                                    }
                                >
                                    {t("PDF")}&nbsp;
                                    <Box component={"span"} sx={{ textTransform: "initial", fontWeight: 400 }}>
                                        -&nbsp;{t("osobne pliki (*{{dot}}zip)", { dot: "." })}
                                    </Box>
                                </Button>
                            ) : null}
                        </Popover>

                        {/* Close */}
                        {!!onClose && (
                            <AppTooltip
                                title={t("Zamknij okno")}
                                arrow
                                placement={"top-end"}
                                type={"dark"}
                                classes={{ tooltip: classes.tooltip }}
                            >
                                <IconButton
                                    onClick={(event) => onClose({ event, show: false })}
                                    aria-label={t("Zamknij okno")}
                                >
                                    <Close color={color} />
                                </IconButton>
                            </AppTooltip>
                        )}
                    </Box>
                </Grid>

                <Grid item xs={12} className={classes.header}>
                    <span style={{ flexGrow: 1 }} />
                    <FormControlLabel
                        value="start"
                        control={
                            <Checkbox
                                checked={CHECKED?.length === data.length || false}
                                indeterminate={!!(CHECKED?.length && CHECKED.length !== data.length)}
                                onClick={handleCheckAll}
                            />
                        }
                        label={CHECKED?.length === data.length ? t("Odznacz wszystko") : t("Zaznacz wszystko")}
                        labelPlacement="start"
                        className={classes.AllFormControlLabel}
                        classes={{ label: classes.AllFormControlLabelLabel }}
                    />
                    <Divider sx={{ width: "100%" }} />
                </Grid>

                <Grid item xs={12} sx={{ overflow: "auto", height: "100%" }} style={{ paddingTop: 0 }}>
                    <OverlayScrollbarsComponent
                        element="span"
                        options={{ scrollbars: { autoHide: "move", autoHideDelay: 666 } }}
                        events={{ scroll: () => {} }}
                        defer
                        className={classes.osScrollbar}
                    >
                        {data?.length ? (
                            data.map((item) => {
                                const _isExpanded = EXPANDED === item.id_zmienna;
                                return (
                                    <Accordion expanded={_isExpanded} elevation={0} key={item.id_zmienna}>
                                        <AccordionSummary
                                            expandIcon={
                                                <IconButton
                                                    size={"small"}
                                                    onClick={() => handleExpand(item.id_zmienna)}
                                                >
                                                    <ExpandMore />
                                                </IconButton>
                                            }
                                            aria-controls={`${item.id_zmienna}-panel-content`}
                                            id={`${item.id_zmienna}-panel-header`}
                                            classes={{
                                                root: classes.summaryRoot,
                                                content: classes.summaryContent,
                                                expanded: classes.expanded,
                                            }}
                                            style={{
                                                cursor: "default",
                                            }}
                                        >
                                            <Typography
                                                variant={_isExpanded ? "h6" : "body1"}
                                                className={classes.titleSecondary}
                                                component={"h3"}
                                            >
                                                {item?.name ? (
                                                    <Box
                                                        component={"a"}
                                                        sx={{ color: "primary.main" }}
                                                        target={"_self"}
                                                        href={`/${i18n.language}/baza-danych?id=${item.id_zmienna}`}
                                                    >
                                                        {translationFromJson(item, "name").replace(/(<([^>]+)>)/gi, "")}
                                                    </Box>
                                                ) : (
                                                    <></>
                                                )}
                                            </Typography>
                                            <Checkbox
                                                checked={CHECKED.includes(`${item.id_zmienna}`)}
                                                onClick={(event) => handleCheck(event)}
                                                name={`${item.id_zmienna}`}
                                                value={item.id_zmienna}
                                                inputProps={{ "aria-label": `${item.id_zmienna}-checked` }}
                                            />
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ px: 0 }}>
                                            <CertificateContent
                                                data={item}
                                                color={color}
                                                target={"accordion"}
                                                showTitle={false}
                                                accordion={true}
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            })
                        ) : (
                            <>{"No data message"}</>
                        )}
                    </OverlayScrollbarsComponent>
                </Grid>
            </Grid>

            {!!EXPORTING ? (
                <Box className={classes.preloader}>
                    <img src="/images/preloader_CircleV01.gif" alt="loading..." />
                </Box>
            ) : null}
        </Box>
    );
};

export default CertificateAccordion;
